import * as m from '@staiter/i18n';
import type { ApprovalMatrixEntry, Role } from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { AlertTriangle, Save } from 'lucide-react';
import { useMemo, useState } from 'react';
import { PageHeader, Table } from '~/components/data';
import { Button } from '~/components/primitives';
import { fetchApprovalMatrix, updateApprovalMatrixEntry } from '~/lib/api/admin';
import { getRoleLabel } from '~/lib/rbac';
import shared from './_auth._shared.module.css';
import styles from './_auth.admin.module.css';

export const Route = createFileRoute('/_auth/admin/approval-matrix')({
  component: ApprovalMatrixAdmin,
});

const ALL_ROLES: Role[] = [
  'accountant',
  'reviewer',
  'sme',
  'warehouse_operator',
  'production_planner',
  'admin',
  'manager',
  'viewer',
];

// SET-AUDIT-3 (pass3 settings audit): aligned с реальными task_types в DB.
// Tier 3 push tasks обязаны требовать минимум 2 подписей per CLAUDE.md §5.
// `stock_reconciliation_submit` + `landed_cost_apply` (renamed → landed_cost_submit)
// — Tier 2, 1 signer достаточен (regular write с named approver, не regulatory push).
const CRITICAL_TASKS = new Set(['counterparty_push_1c', 'esf_push', 'kpn_push']);

function ApprovalMatrixAdmin(): JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'approval-matrix'],
    queryFn: fetchApprovalMatrix,
  });
  const [drafts, setDrafts] = useState<Record<string, Partial<ApprovalMatrixEntry>>>({});

  const items: ApprovalMatrixEntry[] = data?.items ?? [];

  const criticalSingleSigner = useMemo(
    () =>
      items.some(
        (it) =>
          CRITICAL_TASKS.has(it.task_type) && (drafts[it.id]?.min_signers ?? it.min_signers) <= 1,
      ),
    [items, drafts],
  );

  const updateMut = useMutation({
    mutationFn: (input: { id: string; patch: Partial<ApprovalMatrixEntry> }) =>
      updateApprovalMatrixEntry(input.id, input.patch),
    onSuccess: (_, input) => {
      setDrafts((d) => {
        const next = { ...d };
        delete next[input.id];
        return next;
      });
      void qc.invalidateQueries({ queryKey: ['admin', 'approval-matrix'] });
    },
  });

  const setDraft = (id: string, patch: Partial<ApprovalMatrixEntry>): void => {
    setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));
  };

  const toggleRole = (entry: ApprovalMatrixEntry, role: Role): void => {
    const current = drafts[entry.id]?.required_roles ?? entry.required_roles;
    const next = current.includes(role) ? current.filter((r) => r !== role) : [...current, role];
    setDraft(entry.id, { required_roles: next });
  };

  return (
    <div className={shared.page}>
      <PageHeader title={m.approval_matrix_title()} description={m.approval_matrix_description()} />

      {criticalSingleSigner && (
        <div className={styles.warning} role="alert">
          <AlertTriangle size={14} aria-hidden="true" />
          {m.approval_matrix_warning()}
        </div>
      )}

      <Table
        ariaLabel={m.approval_matrix_title()}
        loading={isLoading}
        data={items}
        rowKey={(e) => e.id}
        columns={[
          {
            id: 'task',
            header: m.approval_matrix_col_task(),
            accessor: (e) => <code>{e.task_type}</code>,
          },
          {
            id: 'tier',
            header: m.approval_matrix_col_tier(),
            accessor: (e) => {
              const tier = drafts[e.id]?.tier ?? e.tier;
              return (
                <input
                  type="number"
                  className={[styles.input, shared.widthSm].join(' ')}
                  value={tier}
                  min={0}
                  max={3}
                  onChange={(ev) => setDraft(e.id, { tier: Number(ev.target.value) })}
                  aria-label={m.approval_matrix_tier_aria({ task: e.task_type })}
                />
              );
            },
          },
          {
            id: 'roles',
            header: m.approval_matrix_col_roles(),
            accessor: (e) => {
              const roles = (drafts[e.id]?.required_roles ?? e.required_roles) as Role[];
              return (
                <div className={styles.chips}>
                  {ALL_ROLES.map((r) => (
                    <button
                      key={r}
                      type="button"
                      className={[styles.chip, roles.includes(r) ? styles.chipActive : ''].join(
                        ' ',
                      )}
                      onClick={() => toggleRole(e, r)}
                      aria-pressed={roles.includes(r)}
                    >
                      {getRoleLabel(r)}
                    </button>
                  ))}
                </div>
              );
            },
          },
          {
            id: 'signers',
            header: m.approval_matrix_col_min_signers(),
            accessor: (e) => {
              const v = drafts[e.id]?.min_signers ?? e.min_signers;
              return (
                <input
                  type="number"
                  min={1}
                  max={5}
                  className={[styles.input, shared.widthSm].join(' ')}
                  value={v}
                  onChange={(ev) => setDraft(e.id, { min_signers: Number(ev.target.value) })}
                  aria-label={m.approval_matrix_signers_aria({ task: e.task_type })}
                />
              );
            },
          },
          {
            id: 'save',
            header: '',
            accessor: (e) => {
              const dirty = !!drafts[e.id];
              return (
                <Button
                  size="sm"
                  variant={dirty ? 'primary' : 'ghost'}
                  disabled={!dirty || updateMut.isPending}
                  leadingIcon={<Save size={12} />}
                  onClick={() => updateMut.mutate({ id: e.id, patch: drafts[e.id]! })}
                >
                  {m.common_button_save()}
                </Button>
              );
            },
          },
        ]}
      />
    </div>
  );
}
